import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import { zoneKey } from '../../../core/field/zones';
import {
  CARD_ASPECT, add, canvasOf, card, fade, fill, place,
} from '../fx/common';

/**
 * **ドロー**（Issue #582。指示書 `../direction/draw.md`）。
 *
 * 引いた枚数ぶん、裏のカード（引いた人のスリーブ）が**画面の中央**に現れ、少しずつ間を開けて
 * 「ぱしゅぱしゅ」と引いた人の HUD の手札へ一直線に飛ぶ。デッキからは出さない（左右で動きの差が出すぎる）。
 * ドローフェイズ・効果・初手で見せ方は分けない。何を引いたかは見せない（常に裏）。
 *
 * ```
 * 1 枚ごと（i 枚目は i × 120ms 遅れ）
 * 0     画面の中央に裏のカードが大きさ 0 から開いて現れる
 * 120   手札の「次の 1 枚」の場所へ一直線（ease_out。余裕をもって）。小さくなりながら細い硬い線を 1 本引く
 * 480   着く: スリーブの大きさまで縮みながら消える
 * 560   その 1 枚の終わり
 * ```
 *
 * **最後の 1 枚が着いたところで盤面を進める**（口 `board`。HUD の手札の枚数が増える）。
 * 両方のプレイヤーが続けて引く（初手）ときも重ねない（P1 → P2 の順に流す）。
 */
/** 1 枚の尺 */
export const PER = 560;
/** 次の 1 枚までの間 */
export const GAP = 120;
const APPEAR = 120;
export const ARRIVE = 480;
const TAIL_MS = 70;
/** 中央での幅 */
export const CARD_W = 240;
const FLY_SCALE = 0.5;
const LINE_W = 3;

const side = (p) => (p === 1 ? 1 : 0);
/** 引いた枚数（少なくとも 1） */
export const countOf = (payload) => Math.max(1, Number(payload?.count) || (payload?.cards?.length ?? 0) || 1);
/** 帯の尺 */
export const durationOf = (n) => (Math.max(1, n) - 1) * GAP + PER;
/** 盤面を進める時刻（最後の 1 枚が着いたところ） */
export const commitOf = (n) => (Math.max(1, n) - 1) * GAP + ARRIVE;

/**
 * **i 枚目の着地点**（舞台のピクセル）。HUD の手札はスリーブを外側の端（P1 = 左・P2 = 右）から `step` ずつずらして並べるので、
 * 引く前の枚数 + i 枚目の場所（上限を超えたら最後の場所）。並べ方の分からない HUD なら箱の中心。返すのは `[x, y, スリーブの幅]`
 */
export function landingOf(hand, player, before, i) {
  if (!hand) return null;
  const s = Number.isFinite(hand.scale) ? hand.scale : 1;
  const cw = Number.isFinite(hand.cardW) ? hand.cardW * s : hand.h * CARD_ASPECT;
  if (!Number.isFinite(hand.cardW) || !Number.isFinite(hand.step)) return [hand.x, hand.y, cw];
  const step = hand.step * s;
  const k = Math.max(0, Math.min((hand.max ?? 10) - 1, (before ?? 0) + i));
  const x = side(player) === 1
    ? hand.x + hand.w / 2 - k * step - cw / 2
    : hand.x - hand.w / 2 + k * step + cw / 2;
  return [x, hand.y, cw];
}

/** i 枚目の飛んだ割合（0 = 中央・1 = 着地点）。t は帯の頭から */
export const flightAt = (t, i) => tween(t - i * GAP, APPEAR, ARRIVE, 'ease_out');

/** i 枚目が見えているか */
export const shownAt = (t, i) => {
  const u = t - i * GAP;
  return u >= 0 && u < PER;
};

export default defineEffect({
  situation: 'draw',
  role: 'effect',
  label: 'ドロー（中央から手札へ）',
  color: '#8fb6ff',
  // 枚数 `count` と引く前の手札の枚数 `handBefore` は分岐レイヤー（`editorPorts/duelSituations/draw.js`）が payload に付ける（Issue #589）
  duration: ({ payload }) => durationOf(countOf(payload)),
  sounds: ({ payload }) => Array.from({ length: countOf(payload) }, (_, i) => ({ at: i * GAP, name: 'draw', gain: 0.55 })),
  // 最後の 1 枚が着いたところで盤面を進める（HUD の手札の枚数が増える）
  board: ({ payload }) => ({ at: commitOf(countOf(payload)) }),
  layers: [{
    id: 'cards',
    label: '手札へ飛ぶ裏のカード',
    space: 'screen',
    front: true,
    script: (ctx) => {
      const { THREE } = ctx;
      const { payload } = ctx;
      const root = new THREE.Group();
      const p = side(payload.player);
      const n = countOf(payload);
      const { w: W, h: H } = canvasOf(ctx);
      const hand = ctx.stage?.hud?.hand?.(p) ?? null;
      const rand = seeded(`${ctx.seed}:draw`);
      const ch = CARD_W / CARD_ASPECT;

      const items = Array.from({ length: n }, (_, i) => {
        const from = [W / 2 + (rand() - 0.5) * 28, H / 2 + (rand() - 0.5) * 28];
        const land = landingOf(hand, p, payload.handBefore, i) ?? [p === 1 ? W - 200 : 200, 200, 44];
        const to = [land[0], land[1]];
        const dx = to[0] - from[0];
        // 飛ぶ向きへ少し傾いて出て、着くまでに正立へ戻る
        const rot0 = (rand() - 0.5) * 0.1 + (dx < 0 ? 0.16 : -0.16);
        const g = new THREE.Group();
        add(root, g, 10 + i * 0.1);
        const line = fill(ctx, '#f4f1e6', 1, 1);
        add(root, line, 9.9 + i * 0.1);
        const c = card(ctx, { faceUp: false, player: p, w: CARD_W, brightness: 1 });
        add(g, c.mesh, 10 + i * 0.1);
        return {
          g, line, from, to, rot0, endScale: Math.min(FLY_SCALE, land[2] / CARD_W),
        };
      });
      const posAt = (it, u) => [it.from[0] + (it.to[0] - it.from[0]) * u, it.from[1] + (it.to[1] - it.from[1]) * u];

      return {
        root,
        update: ({ localMs: t }) => {
          items.forEach((it, i) => {
            const ti = t - i * GAP;
            if (!shownAt(t, i)) {
              fade(it.g, 0);
              fade(it.line, 0);
              return;
            }
            const u = flightAt(t, i);
            const [x, y] = posAt(it, u);
            // 大きさ: 現れる（0 → 1）→ 飛ぶ間に 0.5 倍 → 着いてスリーブの幅まで縮む
            let s = ti < APPEAR ? tween(ti, 0, APPEAR, 'expo_out') : 1 - (1 - FLY_SCALE) * u;
            if (ti >= ARRIVE) s = FLY_SCALE + (it.endScale - FLY_SCALE) * seg(ti, ARRIVE, PER);
            place(it.g, x, y, { s, rot: it.rot0 * (1 - u) });
            fade(it.g, ti < ARRIVE ? 1 : 1 - seg(ti, ARRIVE, PER));

            // 硬い線: 先端はカードの中心、尾は少し前の位置。着く手前で細くなって消える
            const tail = posAt(it, flightAt(t - TAIL_MS, i));
            const len = Math.hypot(x - tail[0], y - tail[1]);
            const thin = 1 - seg(ti, ARRIVE - 100, ARRIVE);
            if (ti >= APPEAR && len > 1 && thin > 0.02) {
              place(it.line, (x + tail[0]) / 2, (y + tail[1]) / 2, {
                sx: len, sy: LINE_W * thin, rot: Math.atan2(y - tail[1], x - tail[0]),
              });
              fade(it.line, 1);
            } else {
              fade(it.line, 0);
            }
          });
        },
      };
    },
  }],
}, 'draw_effect.js');
